/**
 * @description API services for clients module
 * @author Omar Sánchez Martínez
 * @since 2025-06-26
 */

import { apiMunzen } from '@/app/api/apiMunzen/apiMunzen';
import { DEFAULT_ITEMS_PER_PAGE, DEFAULT_PAGE } from '@/app/config/pagination.config';
import type { ApiResponse } from '@/common/types';
import type { Client, ClientForm, ClientsResponse } from '../types/client.interface';
import type { ClientAPIFilters } from '../types/clientFilters.interface';

/**
 * @author: Omar Sánchez Martínez
 * @since: 2025-06-26
 * @description: get paginated list of clients
 */
export const fetchClientsService = async (
  params: Partial<ClientAPIFilters> = {},
  options?: { signal?: AbortSignal },
): Promise<ClientsResponse> => {
  const {
    page = DEFAULT_PAGE,
    items_perpage = DEFAULT_ITEMS_PER_PAGE,
    order_by,
    order_direction,
  } = params;

  // Build query string dynamically to avoid sending undefined params
  const searchParams = new URLSearchParams({
    page: page.toString(),
  });

  if (items_perpage) {
    searchParams.append('items_perpage', items_perpage.toString());
  }

  if (order_by) {
    searchParams.append('order_by', order_by);
  }

  if (order_direction) {
    searchParams.append('order_direction', order_direction);
  }

  const url = `/clients?${searchParams.toString()}`;

  const response = await apiMunzen.get<ClientsResponse>(url, { signal: options?.signal });
  return response.data;
};

/**
 * @author: Omar Sánchez Martínez
 * @since: 2025-06-26
 * @description: search clients with pagination using POST endpoint
 */
export const searchClientsService = async (
  params: ClientAPIFilters,
  options?: { signal?: AbortSignal },
): Promise<ClientsResponse> => {
  const { 
    page = DEFAULT_PAGE, 
    items_perpage = DEFAULT_ITEMS_PER_PAGE, 
    search, 
    order_by, 
    order_direction 
  } = params;

  const response = await apiMunzen.post(
    `/clients/search?page=${page}`,
    {
      data_search: search ?? '', // Map 'search' to 'data_search' for API, ensure empty string if undefined
      items_perpage,
      paginated_search: true,
      order_by,
      order_direction,
    },
    { signal: options?.signal },
  );

  return response.data;
};

/**
 * @author: Omar Sánchez Martínez
 * @since: 2025-06-26
 * @description: get client by id
 */
export const fetchClientByIdService = async (id: number): Promise<Client> => {
  const response = await apiMunzen.get<ApiResponse<Client>>(`/clients/${id}`);
  return response.data.data;
};

/**
 * @author: Omar Sánchez Martínez
 * @since: 2025-06-26
 * @description: search clients without pagination (used in billing for autocomplete)
 */
export const searchClientsWithoutPaginationService = async (
  searchValue: string,
): Promise<Client[]> => {
  const response = await apiMunzen.post<ClientsResponse>('/clients/search', {
    data_search: searchValue,
    paginated_search: false,
  });

  return response.data.data;
};

/**
 * @author: Omar Sánchez Martínez
 * @since: 07/07/2025
 * @description: validate if RFC is unique for clients (used in validation)
 */
export const uniqueRfcClientService = async (
  rfc: string,
  signal?: AbortSignal,
  clientId?: number,
): Promise<boolean> => {
  const data = {
    rfc,
    clientId,
  };

  const response = await apiMunzen.post('/clients/rfc-unique', data, { signal });
  return response.data.is_unique;
};

/**
 * @author: Omar Sánchez Martínez
 * @since: 07/07/2025
 * @description: create a new client
 */
export const createClientService = async (clientData: ClientForm): Promise<Client> => {
  const response = await apiMunzen.post<ApiResponse<Client>>('/clients', clientData);
  return response.data.data;
};

/**
 * @author: Omar Sánchez Martínez
 * @since: 07/07/2025
 * @description: update an existing client
 */
export const updateClientService = async (id: number, clientData: ClientForm): Promise<Client> => {
  const response = await apiMunzen.put<ApiResponse<Client>>(`/clients/${id}`, clientData);
  return response.data.data;
};

/**
 * @author: Omar Sánchez Martínez
 * @since: 07/07/2025
 * @description: delete a client
 */
export const deleteClientService = async (id: number): Promise<{ success: boolean }> => {
  const response = await apiMunzen.delete<ApiResponse<{ success: boolean }>>(`/clients/${id}`);
  return response.data;
};

/**
 * @author: Omar Sánchez Martínez
 * @since: 10/07/2025
 * @description: export clients to excel file
 */
export const exportClientsService = async (): Promise<void> => {
  const response = await apiMunzen.get<Blob>('/clients/export', {
    responseType: 'blob',
  });

  // Create blob and download file
  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  // Create download link
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'Clientes.xlsx';

  // Trigger download
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
